import * as THREE from "three";
import { loadNativeCassette, loadNativeBakes, nativeBackingMaterial, applyShellFresnel } from "./native-assets.js";
import { QUALITY, effectiveQuality } from "./render-policy.js";
import { CoverCache } from "./cover-cache.js";
import { Spring, archiveField, smooth, decryption, nativeBlend, returnAngle, returnLift } from "./motion.js";

// Port of NativeSceneCamera / SceneAppearance: 5.2 lane pitch, .62 record pitch,
// 7.33 archive view span, long-lens camera and the original glTF part selection.
export class ArchiveScene {
  constructor(container, onSelect, onDetail) {
    this.container = container;
    this.onSelect = onSelect;
    this.onDetail = onDetail;
    this.dark = false;
    this.detail = 0;
    this.detailTarget = 0;
    this.selected = 0;
    this.count = 0;
    this.shift = 0;
    this.targetShift = 0;
    this.reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.pending = false;
    this.until = 0;
    this.coverKey = "";
    this.turn = { x: 0, y: 0 };
    this.detailSpring = new Spring(0);
    this.railSpring = new Spring(0);
    this.liftSpring = new Spring(0.4);
    this.shoulderSpring = new Spring(0);
    this.selectionStep = 0;
    this.pulses = [];
    this.returning = [];
    this.lastFrame = 0;
    this.archiveWarmColor = new THREE.Color(0xab9779);
    this.clarity = new Spring(0);
    this.cameraAim = null;
    try { this.qualityPreference = localStorage.getItem("rhine:quality") || "auto"; } catch { this.qualityPreference = "auto"; }
    this.scratch = { dummy: new THREE.Object3D(), color: new THREE.Color(), frustum: new THREE.Frustum(), matrix: new THREE.Matrix4(), sphere: new THREE.Sphere(new THREE.Vector3(), 3.8), direction: new THREE.Vector3(), blendDirection: new THREE.Vector3(-0.277, 0.238, 0.931), target: new THREE.Vector3(), right: new THREE.Vector3(), up: new THREE.Vector3(), detailAim: new THREE.Vector3(), upOffset: new THREE.Vector3(0, 1.85, 0), eye: new THREE.Vector3() };
    this.matrixUploads = 0;
    this.colorUploads = 0;
    try {
      this.renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
        powerPreference: "low-power",
      });
      this.renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5));
      this.renderer.outputColorSpace = THREE.SRGBColorSpace;
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = 1.15;
      this.renderer.shadowMap.enabled = false;
      this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      this.setupPost();
      container.prepend(this.renderer.domElement);
      this.scene = new THREE.Scene();
      this.camera = new THREE.PerspectiveCamera(3, 1, 5, 300);
      this.environments = {
        light: this.buildEnvironment(false),
        dark: this.buildEnvironment(true),
        detail: this.buildEnvironment(true, true),
      };
      this.scene.environment = this.environments.light;
      this.ambient = new THREE.HemisphereLight(0xffffff, 0x8b725d, 2.5);
      this.scene.add(this.ambient);
      this.sun = new THREE.DirectionalLight(0xfff0dc, 3.1);
      this.sun.position.set(-12, 18, 9);
      this.sun.castShadow = true;
      this.sun.shadow.mapSize.set(1024, 1024);
      Object.assign(this.sun.shadow.camera, { left: -12, right: 12, top: 12, bottom: -12, near: 1, far: 65 });
      this.sun.shadow.normalBias = 0.012;
      this.sun.shadow.bias = -0.0001;
      this.sun.shadow.radius = 2;
      this.scene.add(this.sun);
      this.warm = new THREE.PointLight(0xffcb71, 18, 12, 1.2);
      this.warm.position.set(-1, 1, 0);
      this.scene.add(this.warm);
      this.spot = new THREE.SpotLight(0xffd2a0, 0, 26, Math.PI / 6, 0.85, 1.2);
      this.spot.position.set(-6, 10, 6);
      this.spot.castShadow = false;
      this.spot.shadow.mapSize.set(1024, 1024);
      this.spot.shadow.bias = -0.0005;
      this.spot.shadow.normalBias = 0.008;
      this.spot.shadow.radius = 3;
      this.spotOffset = new THREE.Vector3(-5, 9, 6);
      this.spotTargetOffset = new THREE.Vector3(0, 1.6, 0);
      this.scene.add(this.spot);
      this.scene.add(this.spot.target);
      this.focus = new THREE.Group();
      this.scene.add(this.focus);
      this.floor = new THREE.Mesh(new THREE.PlaneGeometry(100, 100), new THREE.ShadowMaterial({ opacity: 0.28 }));
      this.floor.rotation.x = -Math.PI / 2;
      this.floor.position.y = -4.63;
      this.floor.receiveShadow = true;
      this.scene.add(this.floor);
      this.arrays = [];
      this.cells = [];
      this.materials = [];
      this.makeDisc();
      this.coverCache = new CoverCache(async (url) => {
        const image = await new THREE.ImageLoader().loadAsync(url);
        let source = image;
        const longest = Math.max(image.width, image.height);
        if (longest > 1024) {
          const canvas = document.createElement("canvas");
          const ratio = 1024 / longest;
          canvas.width = Math.max(1, Math.round(image.width * ratio));
          canvas.height = Math.max(1, Math.round(image.height * ratio));
          canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);
          source = canvas;
        }
        const texture = new THREE.Texture(source);
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
        texture.needsUpdate = true;
        return texture;
      }, (texture) => texture.dispose());
      this.applyQuality();
      this.decryptLines = Array.from({length: 2}, () => {
        const line = new THREE.Mesh(new THREE.PlaneGeometry(1, 0.012), new THREE.MeshBasicMaterial({color: 0x8c9275, depthTest: false}));
        line.renderOrder = 5;
        line.name = "decryption-line";
        line.visible = false;
        this.focus.add(line);
        return line;
      });
      this.load();
      this.resize();
      this.events();
      new ResizeObserver(() => this.resize()).observe(container);
      this.renderer.domElement.addEventListener("webglcontextlost", (e) => {
        e.preventDefault();
        this.fallback("图形上下文已暂停，刷新页面可恢复 3D。");
      });
      this.renderer.domElement.addEventListener("webglcontextrestored", () =>
        location.reload(),
      );
    } catch (error) {
      this.fallback("当前浏览器无法显示 3D，曲库和播放功能仍可使用。");
    }
  }
  // Soft paper studio instead of Three's RoomEnvironment: a vertical gradient dome
  // plus one warm and one cool softbox. The original lights the cases with a baked
  // light field, so reflections should stay low-contrast and almost colourless.
  gradientTexture(dark) {
    const canvas = document.createElement("canvas");
    canvas.width = 8;
    canvas.height = 256;
    const c = canvas.getContext("2d");
    const gradient = c.createLinearGradient(0, 0, 0, 256);
    if (dark) {
      gradient.addColorStop(0, "#3d5157");
      gradient.addColorStop(0.5, "#1b262a");
      gradient.addColorStop(1, "#090f11");
    } else {
      gradient.addColorStop(0, "#ffffff");
      gradient.addColorStop(0.52, "#f3eee4");
      gradient.addColorStop(1, "#c7bfaf");
    }
    c.fillStyle = gradient;
    c.fillRect(0, 0, 8, 256);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
  }
  buildEnvironment(dark, detail = false) {
    const env = new THREE.Scene();
    const domeMap = this.gradientTexture(dark);
    const dome = new THREE.Mesh(
      new THREE.SphereGeometry(60, 32, 16),
      new THREE.MeshBasicMaterial({ map: domeMap, side: THREE.BackSide, depthWrite: false }),
    );
    env.add(dome);
    const warm = new THREE.Mesh(
      new THREE.PlaneGeometry(30, 18),
      new THREE.MeshBasicMaterial({ color: dark ? 0x6d5a41 : 0xfff3df, side: THREE.DoubleSide }),
    );
    warm.position.set(-16, 21, 15);
    warm.lookAt(0, 0, 0);
    env.add(warm);
    const cool = new THREE.Mesh(
      new THREE.PlaneGeometry(26, 32),
      new THREE.MeshBasicMaterial({ color: detail ? 0xdce5e6 : dark ? 0x354447 : 0xf0f4f3, side: THREE.DoubleSide }),
    );
    cool.position.set(23, 5, detail ? 18 : -11);
    cool.lookAt(0, 0, 0);
    env.add(cool);
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    const texture = pmrem.fromScene(env, 0.02).texture;
    pmrem.dispose();
    dome.geometry.dispose();
    domeMap.dispose();
    dome.material.dispose();
    for (const mesh of [warm, cool]) {
      mesh.geometry.dispose();
      mesh.material.dispose();
    }
    return texture;
  }
  // Port of RhiScene::finalFragment: a nine-point binomial softening (centre 4,
  // axes 2, diagonals 1, total 16) plus a paper vignette. The scene is rendered
  // into a linear HDR target so the blur happens before sRGB encoding.
  setupPost() {
    const size = this.renderer.getDrawingBufferSize(new THREE.Vector2());
    const float = this.renderer.extensions.has("EXT_color_buffer_float");
    this.postTarget = new THREE.WebGLRenderTarget(size.x, size.y, {
      type: float ? THREE.HalfFloatType : THREE.UnsignedByteType,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      depthBuffer: true,
      stencilBuffer: false,
    });
    this.postDepth = new THREE.DepthTexture(size.x, size.y);
    this.postDepth.type = THREE.UnsignedIntType;
    this.postTarget.depthTexture = this.postDepth;
    const bloomOptions = {
      type: float ? THREE.HalfFloatType : THREE.UnsignedByteType,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      depthBuffer: false,
      stencilBuffer: false,
    };
    this.bloomA = new THREE.WebGLRenderTarget(
      Math.max(1, Math.round(size.x / 2)),
      Math.max(1, Math.round(size.y / 2)),
      bloomOptions,
    );
    this.bloomB = new THREE.WebGLRenderTarget(
      Math.max(1, Math.round(size.x / 2)),
      Math.max(1, Math.round(size.y / 2)),
      bloomOptions,
    );
    this.brightMaterial = new THREE.ShaderMaterial({
      uniforms: {
        tDiffuse: { value: null },
        uThreshold: { value: 1.15 },
        uKnee: { value: 0.18 },
      },
      vertexShader: `varying vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`,
      fragmentShader: `uniform sampler2D tDiffuse;
        uniform float uThreshold;
        uniform float uKnee;
        varying vec2 vUv;
        void main() {
          vec4 c = texture2D(tDiffuse, vUv);
          vec3 rgb = c.rgb * c.a;
          float level = max(rgb.r, max(rgb.g, rgb.b));
          gl_FragColor = vec4(rgb * smoothstep(uThreshold, uThreshold + uKnee, level), 1.0);
        }`,
      depthTest: false,
      depthWrite: false,
    });
    this.blurMaterial = new THREE.ShaderMaterial({
      uniforms: {
        tDiffuse: { value: null },
        uTexel: { value: new THREE.Vector2(1 / this.bloomA.width, 0) },
      },
      vertexShader: `varying vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`,
      fragmentShader: `uniform sampler2D tDiffuse;
        uniform vec2 uTexel;
        varying vec2 vUv;
        void main() {
          vec4 sum = texture2D(tDiffuse, vUv) * 6.0
            + (texture2D(tDiffuse, vUv + uTexel) + texture2D(tDiffuse, vUv - uTexel)) * 4.0
            + (texture2D(tDiffuse, vUv + uTexel * 2.0) + texture2D(tDiffuse, vUv - uTexel * 2.0));
          gl_FragColor = sum / 16.0;
        }`,
      depthTest: false,
      depthWrite: false,
    });
    this.postCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this.postMaterial = new THREE.ShaderMaterial({
      uniforms: {
        tDiffuse: { value: null },
        tDepth: { value: null },
        tBloom: { value: null },
        uTexel: { value: new THREE.Vector2(1 / size.x, 1 / size.y) },
        uExposure: { value: 0.9 },
        uVignette: { value: 0.1 },
        uBlur: { value: 1 },
        uBloom: { value: 0.25 },
        uDof: { value: 0 },
        uNear: { value: 5 },
        uFar: { value: 300 },
        uFocus: { value: 140 },
        uFocusRange: { value: 5 },
        uMaxBlur: { value: 4.5 },
      },
      vertexShader: `varying vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`,
      fragmentShader: `uniform sampler2D tDiffuse;
        uniform vec2 uTexel;
        uniform float uVignette;
        uniform float uExposure;
        uniform float uBlur;
        uniform float uBloom;
        uniform float uDof;
        uniform float uNear;
        uniform float uFar;
        uniform float uFocus;
        uniform float uFocusRange;
        uniform float uMaxBlur;
        uniform sampler2D tDepth;
        uniform sampler2D tBloom;
        varying vec2 vUv;
        vec4 tap(vec2 offset, float radius) {
          vec4 c = texture2D(tDiffuse, vUv + offset * uTexel * radius);
          return vec4(c.rgb * c.a, c.a);
        }
        vec3 encode(vec3 c) {
          return mix(c * 12.92, 1.055 * pow(max(c, vec3(0.0031308)), vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c));
        }
        void main() {
          float radius = 1.0;
          if (uDof > 0.5) {
            float d = texture2D(tDepth, vUv).x;
            float viewZ = (uNear * uFar) / ((uFar - uNear) * d - uFar);
            float distance = -viewZ;
            float coc = smoothstep(uFocus + uFocusRange, uFocus + uFocusRange * 2.4, distance);
            radius = 1.0 + coc * uMaxBlur;
          }
          vec4 sum = tap(vec2(0.0), radius) * 4.0
            + (tap(vec2(1.0, 0.0), radius) + tap(vec2(-1.0, 0.0), radius) + tap(vec2(0.0, 1.0), radius) + tap(vec2(0.0, -1.0), radius)) * 2.0
            + tap(vec2(1.0, 1.0), radius) + tap(vec2(-1.0, 1.0), radius) + tap(vec2(1.0, -1.0), radius) + tap(vec2(-1.0, -1.0), radius);
          sum = mix(tap(vec2(0.0), radius), sum / 16.0, uBlur);
          float alpha = sum.a;
          vec3 linear = sum.rgb / max(alpha, 1e-5);
          linear += texture2D(tBloom, vUv).rgb * uBloom;
          vec2 q = vUv - 0.5;
          linear *= clamp(1.0 - dot(q, q) * uVignette, 0.0, 1.0);
          // Tone-map once, after bloom. Clipping the scene first destroys HDR highlights.
          vec3 x = max(linear * uExposure, vec3(0.0));
          x = clamp((x * (2.51 * x + 0.03)) / (x * (2.43 * x + 0.59) + 0.14), 0.0, 1.0);
          gl_FragColor = vec4(encode(x) * alpha, alpha);
        }`,
      depthTest: false,
      depthWrite: false,
    });
    this.postScene = new THREE.Scene();
    this.postScene.add(
      (this.postQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.postMaterial)),
    );
  }
  resizePost() {
    if (!this.postTarget) return;
    const size = this.renderer.getDrawingBufferSize(new THREE.Vector2());
    this.postTarget.setSize(size.x, size.y);
    this.bloomA.setSize(Math.max(1, Math.round(size.x / 2)), Math.max(1, Math.round(size.y / 2)));
    this.bloomB.setSize(Math.max(1, Math.round(size.x / 2)), Math.max(1, Math.round(size.y / 2)));
    this.postMaterial.uniforms.uTexel.value.set(1 / size.x, 1 / size.y);
  }
  renderPost() {
    const renderer = this.renderer;
    const mapping = renderer.toneMapping;
    this.postMaterial.uniforms.uExposure.value = renderer.toneMappingExposure;
    renderer.toneMapping = THREE.NoToneMapping;
    renderer.setRenderTarget(this.postTarget);
    try { renderer.render(this.scene, this.camera); }
    finally { renderer.toneMapping = mapping; }
    this.postQuad.material = this.brightMaterial;
    this.brightMaterial.uniforms.tDiffuse.value = this.postTarget.texture;
    renderer.setRenderTarget(this.bloomA);
    renderer.render(this.postScene, this.postCamera);
    this.postQuad.material = this.blurMaterial;
    this.blurMaterial.uniforms.tDiffuse.value = this.bloomA.texture;
    this.blurMaterial.uniforms.uTexel.value.set(1 / this.bloomA.width, 0);
    renderer.setRenderTarget(this.bloomB);
    renderer.render(this.postScene, this.postCamera);
    this.blurMaterial.uniforms.tDiffuse.value = this.bloomB.texture;
    this.blurMaterial.uniforms.uTexel.value.set(0, 1 / this.bloomB.height);
    renderer.setRenderTarget(this.bloomA);
    renderer.render(this.postScene, this.postCamera);
    this.postQuad.material = this.postMaterial;
    this.postMaterial.uniforms.tDiffuse.value = this.postTarget.texture;
    this.postMaterial.uniforms.tDepth.value = this.postDepth;
    this.postMaterial.uniforms.tBloom.value = this.bloomA.texture;
    renderer.setRenderTarget(null);
    renderer.render(this.postScene, this.postCamera);
  }
  fallback(message) {
    const el = document.getElementById("sceneFallback");
    el.textContent = message;
    el.hidden = false;
    this.container.dataset.renderer = "fallback";
  }
  async load() {
    try {
      const [scene, bakes] = await Promise.all([loadNativeCassette(), loadNativeBakes()]);
      this.nativeBakes = bakes;
      const hidden = [
        "Carbon_Ink",
        "Internal_Ceramic",
        "Optical_Edges",
        "Subsurface_Optics",
        "Optical_Film_Edge",
        "Amber_Optical_Inlay",
        "Champagne_Index",
        "Printed_Label",
      ];
      const repeated = [
        "Frosted_Polymer",
        "Ivory_Edges",
        "Optical_Diffuser",
      ];
      scene.updateMatrixWorld(true);
      for (let lane = -3; lane <= 5; lane++)
        for (let row = -15; row <= 17; row++) this.cells.push({ lane, row });
      scene.traverse((obj) => {
        if (!obj.isMesh) return;
        const name = obj.material.name.replace(/\.\d+$/, "");
        const geometry = obj.geometry.clone().applyMatrix4(obj.matrixWorld);
        if (repeated.includes(name)) {
          const mat = new THREE.MeshStandardMaterial({
            color: 0xe7d6c2,
            roughness: name === "Frosted_Polymer" ? 0.62 : name === "Ivory_Edges" ? 0.58 : 0.66,
            metalness: 0.0,
            envMapIntensity: 0.85,
          });
          const inst = new THREE.InstancedMesh(
            geometry,
            mat,
            this.cells.length,
          );
          inst.userData.part = name;
          inst.receiveShadow = true;
          inst.castShadow = name === "Optical_Diffuser";
          inst.frustumCulled = false;
          this.arrays.push(inst);
          this.scene.add(inst);
        }
        if (hidden.includes(name)) return;
        const mat = name === "Optical_Diffuser" ? nativeBackingMaterial(bakes) : obj.material.clone();
        if (name === "Frosted_Polymer" || name === "Ivory_Edges" || name === "Optical_Film") {
          // Rhine composites transmission through its own backs pass. Three's
          // screen-space transmission blurs the print and turns the rear shell white.
          // Recreate the edge sheen with a thin clearcoat layer over the opaque body.
          const physical = new THREE.MeshPhysicalMaterial({
            color: 0xfffdfa,
            roughness: name === "Ivory_Edges" ? 0.34 : 0.26,
            metalness: 0.0,
            transmission: 0,
            clearcoat: 0.55,
            clearcoatRoughness: 0.24,
            envMapIntensity: 1.1,
            transparent: true,
            opacity: name === "Ivory_Edges" ? 0.2 : 0.11,
            depthWrite: false,
            side: THREE.FrontSide,
          });
          applyShellFresnel(physical);
          mat.dispose();
          obj.userData.webMaterial = physical;
        } else {
          if (name === "Optical_Diffuser") mat.roughness = 0.7;
          obj.userData.webMaterial = mat;
        }
        const mesh = new THREE.Mesh(geometry, obj.userData.webMaterial);
        mesh.userData.part = name;
        mesh.userData.baseRoughness = mesh.material.roughness;
        mesh.userData.baseMetalness = mesh.material.metalness;
        if (["Frosted_Polymer", "Ivory_Edges", "Optical_Film"].includes(name)) {
          mesh.userData.optical = mesh.material;
          mesh.userData.solid = new THREE.MeshPhysicalMaterial({
            roughness: 0.52,
            metalness: 0.02,
            clearcoat: 0.3,
            clearcoatRoughness: 0.38,
            envMapIntensity: 0.95,
          });
          mesh.userData.clear = new THREE.MeshPhysicalMaterial({
            roughness: 0.2,
            metalness: 0.0,
            clearcoat: 0.55,
            clearcoatRoughness: 0.2,
            envMapIntensity: 1.3,
            transparent: true,
            opacity: 0.075,
            depthWrite: false,
            side: THREE.FrontSide,
          });
          applyShellFresnel(mesh.userData.clear);
          mesh.material = mesh.userData.solid;
        }
        mesh.castShadow = name === "Optical_Diffuser";
        mesh.receiveShadow = true;
        this.focus.add(mesh);
        this.materials.push(mesh);
      });
      this.makeLabel();
      this.makeDecoration();
      this.updateTheme();
      this.updateInstances();
      this.container.dataset.renderer = "webgl";
      this.container.dataset.model = "installed-native-cassette";
      this.invalidate(900);
    } catch (error) {
      console.error("Rhine model:", error);
      this.fallback("唱片模型加载失败，曲库和播放功能仍可使用。");
    }
  }
  makeDisc() {
    const geometry = new THREE.RingGeometry(0.23, 1.51, 96);
    const uv = geometry.attributes.uv;
    const pos = geometry.attributes.position;
    for (let i = 0; i < uv.count; i++)
      uv.setXY(i, pos.getX(i) / 3.02 + 0.5, pos.getY(i) / 3.02 + 0.5);
    // Printed artwork is ink under the shell: keep it legible independently of
    // the archive lighting and reserve the optical response for the case.
    this.discMaterial = new THREE.MeshBasicMaterial({
      color: 0xe8e1d0,
      side: THREE.DoubleSide,
    });
    this.disc = new THREE.Mesh(geometry, this.discMaterial);
    // The original shell reaches z=.218. Keep printed artwork just in front of it:
    // screen-space transmission otherwise filters the cover in high quality mode.
    this.disc.position.set(0, 1.85, 0.225);
    this.focus.add(this.disc);
    const hub = new THREE.Mesh(
      new THREE.RingGeometry(0.085, 0.23, 48),
      new THREE.MeshStandardMaterial({
        color: 0x7b8178,
        metalness: 0.5,
        roughness: 0.3,
      }),
    );
    hub.position.set(0, 1.85, 0.232);
    this.focus.add(hub);
    const rim = new THREE.Mesh(new THREE.RingGeometry(1.51, 1.57, 96), new THREE.MeshStandardMaterial({ color: 0xb8c4c2, metalness: 0.8, roughness: 0.2, side: THREE.DoubleSide }));
    rim.position.set(0, 1.85, 0.228);
    this.focus.add(rim);
    this.discParts = [this.disc, hub, rim];
  }
  makeLabel() {
    this.labelCanvas = document.createElement("canvas");
    this.labelCanvas.width = 1024;
    this.labelCanvas.height = 340;
    this.labelTexture = new THREE.CanvasTexture(this.labelCanvas);
    this.labelTexture.colorSpace = THREE.SRGBColorSpace;
    const material = new THREE.MeshBasicMaterial({
      map: this.labelTexture,
      transparent: true,
      depthWrite: false,
    });
    this.label = new THREE.Mesh(new THREE.PlaneGeometry(1.25, 0.415), material);
    this.label.position.set(-1.25, 3.02, 0.255);
    this.focus.add(this.label);
    this.paintLabel();
  }
  paintLabel() {
    if (!this.labelCanvas) return;
    const c = this.labelCanvas.getContext("2d");
    c.clearRect(0, 0, 1024, 340);
    c.fillStyle = this.dark ? "#101d21" : "#292d28";
    // Native record labels are dark printed plates, not floating white lettering.
    if (this.dark) c.fillRect(0, 0, 1024, 310);
    c.fillStyle = this.dark ? "#e4e8df" : "#424940";
    c.font = "700 76px Rhine, sans-serif";
    c.fillText((this.song?.title || "RHINE MUSIC").slice(0, 28), 35, 96, 955);
    c.font = "40px Rhine, sans-serif";
    c.fillText(
      (this.song?.artist || "MUSIC DATABASE").slice(0, 45),
      35,
      175,
      955,
    );
    c.font = "28px Rhine, sans-serif";
    c.fillText("SONG ID / " + (this.song?.id || "LOCAL ARCHIVE"), 35, 248, 955);
    c.fillRect(35, 286, 300, 4);
    this.labelTexture.needsUpdate = true;
    this.paintDecoration();
  }
  makeDecoration() {
    this.decorationCanvas = document.createElement("canvas");
    this.decorationCanvas.width = 2000; this.decorationCanvas.height = 1480;
    this.decorationTexture = new THREE.CanvasTexture(this.decorationCanvas);
    this.decorationTexture.colorSpace = THREE.SRGBColorSpace;
    const material = new THREE.MeshBasicMaterial({ map: this.decorationTexture, transparent: true, depthWrite: false });
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(5, 3.7), material);
    mesh.position.set(0, 1.85, 0.26);
    this.focus.add(mesh);
    this.paintDecoration();
  }
  paintDecoration() {
    if (!this.decorationCanvas) return;
    // Canvas coordinates ported from RhiScene::paintDecoration (Rhine, MIT).
    const c = this.decorationCanvas.getContext("2d");
    c.setTransform(2, 0, 0, 2, 0, 0); c.clearRect(0, 0, 1000, 740);
    c.strokeStyle = c.fillStyle = this.dark ? "#b7c0bb80" : "#41463eaa";
    c.lineWidth = 1.4;
    for (const [x,y] of [[75,60],[930,60],[75,685],[930,685]]) {
      c.beginPath(); c.moveTo(x-10,y); c.lineTo(x+10,y); c.moveTo(x,y-10); c.lineTo(x,y+10); c.stroke();
    }
    for (const radius of [27.5,11.5]) { c.beginPath(); c.ellipse(902.5,601.5,radius,27.5,0,0,Math.PI*2); c.stroke(); }
    c.beginPath(); c.moveTo(875,601); c.lineTo(930,601); c.stroke(); c.fillRect(875,654,25,5);
    c.font = "15px Rhine, sans-serif"; c.fillText("MUSIC ARCHIVE / 001",130,688);
    // RHINE LAB lettering is already present in the model geometry.
    this.decorationTexture.needsUpdate = true;
  }
  updateInstances(time = performance.now() / 1000) {
    if (this.mobileStage) return;
    const { dummy, color, frustum, matrix, sphere } = this.scratch;
    const themeActive = time < (this.themeStart || 0) + 1.8;
    const moving = this.railSpring.moving(-this.selectionStep * .62) || this.shoulderSpring.moving(this.selectionStep) || this.pulses.length || this.idleGain;
    const signature = [this.selectionStep, this.returning.map(a=>a.row).join(','), this.camera.position.x.toFixed(3), this.camera.position.y.toFixed(3), this.camera.position.z.toFixed(3), this.camera.fov.toFixed(3), this.width, this.height, this.dark].join('|');
    if (!moving && !themeActive && signature === this.instanceSignature) return;
    this.instanceSignature = signature;
    frustum.setFromProjectionMatrix(matrix.multiplyMatrices(this.camera.projectionMatrix, this.camera.matrixWorldInverse));
    const baseRow = Math.round(-this.railSpring.value / 0.62);
    const visible = [];
    const visibleSlots = this.visibleSlots || [];
    const colorStateChanged = themeActive || this.themeColorsDirty || moving || this.lastColorSelection !== this.selectionStep || this.lastColorDark !== this.dark;
    let colorChanged = false;
    this.cells.forEach(({ lane, row }, i) => {
      const absoluteRow = row + baseRow;
      const near =
        lane === 0 &&
        (absoluteRow === this.selectionStep ||
          this.returning.some((a) => a.row === absoluteRow));
      dummy.position.set(
        lane * 5.2,
        -4.6 +
          archiveField(
            absoluteRow - this.shoulderSpring.value,
            lane,
            time,
            this.pulses,
            this.idleGain,
          ),
        absoluteRow * 0.62 - 2.17 + this.railSpring.value,
      );
      dummy.scale.setScalar(near ? 0.001 : 1);
      sphere.center.copy(dummy.position).add(this.scratch.eye.set(0,1.85,0));
      if (!frustum.intersectsSphere(sphere)) return;
      dummy.updateMatrix();
      const slot = visible.length;
      visible.push(this.cells[i]);
      const slotColorChanged = colorStateChanged || visibleSlots[slot] !== i;
      visibleSlots[slot] = i;
      for (const mesh of this.arrays) {
        mesh.setMatrixAt(slot, dummy.matrix);
        if (mesh.userData.themeTo && slotColorChanged) {
          const wave = this.reduced ? 1 : smooth((time - (this.themeStart || 0) - Math.hypot(row, lane * 2) * 0.035) / 0.7);
          const distance = Math.abs(absoluteRow - this.selectionStep);
          const fade = this.dark
            ? Math.min(0.78, Math.max(0, distance - 2) * 0.085)
            : Math.min(0.58, Math.max(0, distance - 4) * 0.055);
          color.copy(mesh.userData.themeFrom).lerp(mesh.userData.themeTo, wave).lerp(this.backgroundColor, fade);
          if (this.dark && absoluteRow !== this.selectionStep) {
            const glow = Math.exp(-distance * 0.82 - Math.abs(lane) * 1.1) * 0.16;
            color.lerp(this.archiveWarmColor, glow);
          }
          mesh.setColorAt(slot, color);
          colorChanged = true;
        }
      }
    });
    visibleSlots.length = visible.length;
    this.visibleSlots = visibleSlots;
    for (const mesh of this.arrays) {
      mesh.instanceMatrix.needsUpdate = true;
      mesh.count = visible.length;
      mesh.userData.visibleCells = visible;
      if (mesh.instanceColor && colorChanged) { mesh.instanceColor.needsUpdate = true; this.colorUploads++; }
    }
    this.lastColorSelection = this.selectionStep;
    this.lastColorDark = this.dark;
    this.themeColorsDirty = themeActive;
    this.matrixUploads++;
  }
  updateTheme() {
    if (!this.scene) return;
    const dark = this.dark;
    if (this.nativeBakes) this.nativeBakes.theme.value = dark ? 1 : 0;
    this.backgroundColor = new THREE.Color(dark ? 0x0a0f14 : 0xe7e7e1);
    this.scene.fog = new THREE.Fog(dark ? 0x0a0f14 : 0xe7e7e1, 145, 173);
    if (this.environments) this.scene.environment = this.environments[dark ? "dark" : "light"];
    this.renderer.toneMappingExposure = dark ? 0.9 : 0.86;
    if (this.postMaterial) this.postMaterial.uniforms.uVignette.value = dark ? 0.14 : 0.1;
    this.scene.environmentIntensity = dark ? 0.42 : 0.6;
    this.ambient.groundColor.set(dark ? 0x151d1f : 0xd2cabc);
    this.ambient.intensity = dark ? 0.42 : 0.55;
    this.sun.intensity = dark ? 0.7 : 1.0;
    this.sun.color.set(dark ? 0xc3d6da : 0xfff0dc);
    // Keep the warm accent near the selected sleeve instead of lighting the row.
    this.warm.intensity = dark ? 1.2 : 1.2;
    this.warm.distance = dark ? 8.5 : 12;
    if (this.spot) {
      this.spot.intensity = dark ? 18 : 22;
      this.spot.distance = dark ? 26 : 18;
      this.spot.angle = dark ? Math.PI / 6 : Math.PI / 5;
      this.spot.castShadow = Boolean(this.profile?.shadows);
    }
    for (const mesh of this.arrays) {
      const name = mesh.userData.part;
      const previous = mesh.material.color.clone();
      if (mesh.instanceColor) mesh.getColorAt(Math.floor(this.cells.length / 2), previous);
      mesh.material.color.set(
        name === "Optical_Diffuser" ? (dark ? 0x252b2b : 0xb4a184) : name === "Index_Inlay"
          ? dark
            ? 0xbfa15f
            : 0xd9c9ad
          : name === "Titanium_Fasteners"
            ? 0xaab4af
            : dark
              ? 0x131d1f
              : name === "Frosted_Polymer" ? 0xc3bdb1 : 0xdcd6c9,
      );
      mesh.userData.themeFrom = previous;
      mesh.userData.themeTo = mesh.material.color.clone();
      mesh.material.color.set(0xffffff);
    }
    for (const mesh of this.materials) {
      const name = mesh.userData.part;
      delete mesh.userData.archiveDarkColor;
      mesh.material.envMap = null;
      mesh.material.roughness = mesh.userData.baseRoughness;
      mesh.material.metalness = mesh.userData.baseMetalness;
      mesh.material.color.set(
        name === "Index_Inlay"
          ? dark
            ? 0xc4a05c
            : 0xd8c6a5
          : name.includes("Engraving") || name === "Moulded_Lettering"
            ? dark
              ? 0x9caeab
              : 0x999487
            : name === "Titanium_Fasteners"
              ? 0xbfc5bd
              : dark
                ? 0x2a3638
                : 0xf2ece4,
      );
      if (mesh.userData.optical) {
        mesh.userData.optical.envMap = mesh.userData.clear.envMap = null;
        mesh.userData.clear.opacity = 0.075;
        mesh.userData.solid.color.set(dark ? 0x36474a : 0xc5beb0);
        mesh.userData.clear.color.set(dark ? 0xb0bdb9 : 0xe8e4da);
        mesh.userData.optical.color.set(dark ? 0x8ea19d : 0xfffdfa);
        mesh.userData.optical.opacity = mesh.userData.part === "Ivory_Edges"
          ? dark ? 0.13 : 0.22
          : dark ? 0.075 : 0.12;
      }
    }
    this.discMaterial.color.set(
      this.discMaterial.map ? 0xffffff : dark ? 0x8a9386 : 0xe8dfce,
    );
    this.paintLabel();
    this.themeStart = performance.now() / 1000;
    this.invalidate(2600);
  }
  setTheme(dark) {
    this.dark = dark;
    this.updateTheme();
  }
  applyQuality() {
    if (!this.renderer) return;
    this.quality = effectiveQuality(this.qualityPreference, Boolean(this.mobileStage), this.autoSlow);
    this.profile = QUALITY[this.quality];
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, this.profile.ratio));
    this.renderer.shadowMap.enabled = this.profile.shadows;
    if (this.spot) this.spot.castShadow = this.profile.shadows;
    this.postEnabled = Boolean(this.profile.post && this.postTarget);
    this.resizePost();
    this.instanceSignature = null;
  }
  setQuality(value) {
    this.qualityPreference = ["auto","high","balanced","low"].includes(value) ? value : "auto";
    this.autoSlow = false;
    try { localStorage.setItem("rhine:quality", this.qualityPreference); } catch {}
    this.applyQuality(); this.invalidate(2000);
  }
  setReduced(value) {
    this.reduced = value;
    this.invalidate();
  }
  setDetail(value) {
    if (!value) this.decryptElapsed = null;
    this.detailTarget = value ? 1 : 0;
    if (this.renderer) this.renderer.domElement.style.touchAction = "none";
    this.invalidate(1200);
  }
  select(song, index, count) {
    this.lastInteraction = performance.now();
    const changed = song?.key !== this.song?.key;
    if (changed && this.song && this.materials?.length && !this.reduced)
      this.returnCurrent();
    if (changed) {
      this.decryptElapsed = null;
      this.clarity = new Spring(0);
      let delta = index - this.selected;
      if (count !== this.count) {
        this.selectionStep = 0;
        this.railSpring = new Spring(0);
        this.shoulderSpring = new Spring(0);
      } else {
        if (delta > count / 2) delta -= count;
        if (delta < -count / 2) delta += count;
        this.selectionStep += delta;
      }
      this.liftSpring = new Spring(0);
      if (!this.reduced)
        this.pulses.push({ row: 0, time: performance.now() / 1000 });
      this.invalidate(1900);
    }
    this.song = song;
    this.count = count;
    this.selected = index;
    if (!this.discMaterial) return;
    this.targetShift = 0;
    this.selected = index;
    this.paintLabel();
    this.focusPulse = performance.now();
    this.invalidate(1000);
    const cover = song?.cover || "";
    if (cover === this.coverKey) return;
    this.coverKey = cover;
    this.coverToken = (this.coverToken || 0) + 1;
    const token = this.coverToken;
    this.coverCache?.setWanted([cover]);
    this.discMaterial.map = null;
    this.discMaterial.color.set(this.dark ? 0x8a9386 : 0xe8dfce);
    this.discMaterial.needsUpdate = true;
    if (cover)
      this.coverCache?.request(cover, 10).then((texture) => {
        if (token !== this.coverToken || !texture) return;
        this.discMaterial.map = texture;
        this.discMaterial.color.set(0xffffff);
        this.discMaterial.needsUpdate = true;
        this.invalidate();
      }).catch(() => {});
  }
  prefetchCovers(list, index) {
    if (!this.coverCache || !list?.length) return;
    const at = (offset) => list[(index + offset + list.length) % list.length]?.cover;
    this.coverCache.setWanted([at(0), at(1), at(-1)]);
  }
  returnCurrent() {
    const object = this.focus.clone(true);
    const materials = [];
    object.traverse((child) => {
      if (!child.isMesh) return;
      const sourceMaterial = child.material;
      child.material = sourceMaterial.clone();
      child.material.onBeforeCompile = sourceMaterial.onBeforeCompile;
      child.material.customProgramCacheKey = sourceMaterial.customProgramCacheKey;
      if (child.name === "decryption-line") child.visible = false;
      materials.push(child.material);
      if (child.material.map) {
        const original = child.material.map;
        let texture;
        if (original.isCanvasTexture) {
          const canvas = document.createElement("canvas");
          canvas.width = original.image.width;
          canvas.height = original.image.height;
          canvas.getContext("2d").drawImage(original.image, 0, 0);
          texture = new THREE.CanvasTexture(canvas);
        } else texture = original.clone();
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.needsUpdate = true;
        child.material.map = texture;
      }
    });
    this.scene.add(object);
    this.returning.push({
      object,
      materials,
      row: this.selectionStep,
      lift: new Spring(this.liftSpring.value),
      returnY: object.position.y,
    });
    this.returning.at(-1).lift.velocity = this.liftSpring.velocity;
    while (this.returning.length > 4)
      this.disposeReturning(this.returning.shift());
  }
  disposeReturning(actor) {
    this.scene.remove(actor.object);
    for (const material of actor.materials) {
      material.map?.dispose();
      material.dispose();
    }
  }
  resize() {
    if (!this.renderer) return;
    this.width = Math.max(1, this.container.clientWidth);
    this.height = Math.max(1, this.container.clientHeight);
    this.renderer.setSize(this.width, this.height, false);
    this.applyQuality();
    this.resizePost();
    this.invalidate(200);
  }
  invalidate(duration = 100) {
    clearTimeout(this.idleTimer);
    this.until = Math.max(this.until, performance.now() + duration);
    if (!this.pending && !document.hidden && !this.obscured) {
      if (performance.now() - this.lastFrame > 200)
        this.lastFrame = performance.now();
      this.pending = true;
      requestAnimationFrame((t) => this.frame(t));
    }
  }
  frame(now) {
    this.pending = false;
    if (document.hidden || !this.renderer || this.obscured) return;
    const interval = now - this.lastFrame;
    if (this.lastFrame && interval < 200 && !this.reduced) {
      this.slowFrames = interval > 30 ? (this.slowFrames || 0) + 1 : Math.max(0,(this.slowFrames || 0)-1);
      if (this.qualityPreference === "auto" && !this.autoSlow && this.slowFrames > 75) { this.autoSlow = true; this.applyQuality(); }
    }
    const seconds = Math.min(
      0.05,
      Math.max(0.001, (now - this.lastFrame) / 1000),
    );
    this.lastFrame = now;
    this.idleGain = this.profile.idle && !this.reduced && !this.mobileStage && !this.detailTarget && !this.returning.length
      ? smooth((now - (this.lastInteraction || 0) - 2500) / 1000) : 0;
    const aligning = !this.detailTarget && Math.hypot(this.turn.x, this.turn.y) > 0.005;
    if (!this.dragging && !this.detailTarget) {
      this.turn.x = returnAngle(this.turn.x, seconds, this.reduced);
      this.turn.y = returnAngle(this.turn.y, seconds, this.reduced);
    }
    this.railSpring.step(-this.selectionStep * 0.62, 3.7, seconds, this.reduced);
    this.shoulderSpring.step(this.selectionStep, 5, seconds, this.reduced);
    if (!aligning) this.liftSpring.step(this.detailTarget ? 4.05 : 0.4, 4.2, seconds, this.reduced);
    const cameraTarget = aligning ? this.detail : this.detailTarget
      ? smooth((this.liftSpring.value - 0.8) / 2.4)
      : smooth((this.liftSpring.value - 0.4) / 3.65);
    this.detail = nativeBlend(this.detail, cameraTarget, 2.8, seconds, this.reduced);
    const reveal = this.detail > 0.35 && this.liftSpring.value > 1.55;
    if (reveal && this.decryptElapsed == null) this.decryptElapsed = 0;
    if (this.decryptElapsed != null && this.detailTarget) this.decryptElapsed += seconds;
    const decrypt = decryption(this.reduced ? 4 : 0.8 + (this.decryptElapsed || 0) * 2.6);
    this.clarity.value = this.detailTarget ? decrypt.clarity : this.clarity.value * Math.exp(-seconds * 9);
    if (this.reduced) this.clarity.value = this.detailTarget;
    if (this.clarity.value < 0.0001) this.clarity.value = 0;
    this.decryptLines.forEach((line,i) => {
      const interval = !this.reduced && this.detailTarget && decrypt.intervals[i];
      line.visible = Boolean(interval);
      if (interval) {
        line.scale.x = (interval[1]-interval[0])*5;
        line.position.set(-2.5+(interval[0]+interval[1])*2.5,3.7,0.28);
      }
    });
    this.container.style.setProperty("--detail-reveal", this.clarity.value);
    document.documentElement.style.setProperty("--detail-reveal", this.clarity.value);
    this.pulses = this.reduced
      ? []
      : this.pulses.filter((p) => now / 1000 - p.time < 3.2);
    const d = this.detail;
    // Limit this calibration to the desktop dark detail sample. The archive and
    // mobile retain their own lighting until their dedicated review stages.
    const desktopDetail = this.dark && !this.mobileStage && this.width > 900 &&
      this.width / this.height >= 1.2 ? smooth(d) : 0;
    this.warm.intensity = THREE.MathUtils.lerp(this.dark ? 20 : 2.4, 11, desktopDetail);
    if (this.nativeBakes) this.nativeBakes.reveal.value = desktopDetail;
    this.scene.environmentIntensity = this.dark ? THREE.MathUtils.lerp(0.34, 0.65, desktopDetail) : 0.6;
    this.spot.intensity = this.dark ? THREE.MathUtils.lerp(30, 6, desktopDetail) : 22;
    if (this.dark) {
      const sleeveWarmth = 1 - smooth(d / 0.75);
      for (const mesh of this.materials) {
        if (mesh.userData.optical) {
          mesh.userData.solid.color.set(0x36474a).lerp(this.archiveWarmColor, sleeveWarmth * 0.7);
          for (const layer of [mesh.userData.optical, mesh.userData.clear]) {
            layer.envMap = desktopDetail > 0 ? this.environments.detail : null;
            const edge = mesh.userData.part === "Ivory_Edges";
            const baseOpacity = layer === mesh.userData.clear ? 0.075 : edge ? 0.13 : 0.075;
            layer.opacity = THREE.MathUtils.lerp(baseOpacity, edge ? 0.24 : 0.065, desktopDetail);
          }
          continue;
        }
        if (mesh.userData.part === "Optical_Diffuser" || mesh.userData.part === "Frosted_Polymer") {
          mesh.material.color.copy(mesh.userData.archiveDarkColor || (mesh.userData.archiveDarkColor = mesh.material.color.clone()))
            .lerp(this.archiveWarmColor, sleeveWarmth * 0.7);
          // Neutral backing receives the warm key light; don't bake gold into
          // every surface, which also turns the unlit side yellow.
          mesh.material.color.lerp(this.scratch.color.set(0x55534d), desktopDetail);
        }
        if (mesh.userData.part.includes("Engraving") || mesh.userData.part === "Moulded_Lettering") {
          mesh.material.color.set(0x9caeab).lerp(this.scratch.color.set(0x9ba6a8), desktopDetail);
          mesh.material.roughness = THREE.MathUtils.lerp(mesh.userData.baseRoughness, 0.48, desktopDetail);
          mesh.material.metalness = THREE.MathUtils.lerp(mesh.userData.baseMetalness, 0.2, desktopDetail);
          mesh.material.envMap = desktopDetail > 0 ? this.environments.detail : null;
          mesh.material.emissive?.setHex(desktopDetail > 0 ? 0x202722 : 0x000000);
        }
      }
    }
    if (this.postMaterial) this.postMaterial.uniforms.uBlur.value = 1 - 0.84 * d;
    this.floor.visible = !this.mobileStage;
    const portrait = this.width / this.height < 1.05;
    const aspect = this.width / this.height;
    const span = this.mobileStage ? Math.max(4.7, 5.6 / aspect) : portrait
      ? THREE.MathUtils.lerp(8.4 / aspect, 6.3 / aspect, d)
      : THREE.MathUtils.lerp(7.33, THREE.MathUtils.lerp(5.9, 6.1, desktopDetail), d) * Math.max(1, 16 / 9 / aspect);
    const distance = THREE.MathUtils.lerp(140, 72, d);
    const yaw = THREE.MathUtils.degToRad(59), elevation = THREE.MathUtils.degToRad(19);
    const direction = this.scratch.direction.set(-Math.sin(yaw)*Math.cos(elevation), Math.sin(elevation), Math.cos(yaw)*Math.cos(elevation))
      .lerp(this.scratch.blendDirection, d)
      .normalize();
    const target = this.scratch.target.set(-1.091, -0.045, 0.481);
    const right = this.scratch.right.set(0, 1, 0).cross(direction).normalize();
    const up = this.scratch.up.copy(direction).cross(right).normalize();
    this.focus.position.set(
      0,
      -4.6 +
        archiveField(
          this.selectionStep - this.shoulderSpring.value,
          0,
          now / 1000,
          this.pulses,
          this.idleGain,
        ) +
        this.liftSpring.value,
      -2.17 + this.selectionStep * 0.62 + this.railSpring.value,
    );
    for (let i = this.returning.length - 1; i >= 0; i--) {
      const actor = this.returning[i];
      const aligned = Math.hypot(actor.object.rotation.x, actor.object.rotation.y) < 0.005;
      if (aligned) {
        actor.lift.value = returnLift(actor.lift.value, seconds, this.reduced);
        actor.lift.velocity = 0;
      }
      actor.object.position.set(
        0,
        -4.6 +
          archiveField(
            actor.row - this.shoulderSpring.value,
            0,
            now / 1000,
            this.pulses,
          ) +
          actor.lift.value,
        -2.17 + actor.row * 0.62 + this.railSpring.value,
      );
      actor.object.rotation.x = returnAngle(actor.object.rotation.x, seconds, this.reduced);
      if (!aligned) actor.object.position.y = actor.returnY;
      actor.object.rotation.y = returnAngle(actor.object.rotation.y, seconds, this.reduced);
      if (!actor.lift.moving(0)) {
        this.disposeReturning(actor);
        this.returning.splice(i, 1);
      }
    }
    this.focus.rotation.set(this.turn.x * d, this.turn.y * d, 0);
    for (const part of this.discParts) part.visible = this.clarity.value > 0.01;
    this.disc.scale.setScalar(0.97 + 0.03 * this.clarity.value);
    for (const mesh of this.materials) {
      if (mesh.userData.optical) {
        const clear = this.clarity.value > 0.98;
        const material = clear ? this.profile.gloss ? mesh.userData.optical : mesh.userData.clear : mesh.userData.solid;
        mesh.material = material;
      }
    }
    for (const mesh of this.arrays) mesh.visible = !this.mobileStage;
    const detailAim = this.scratch.detailAim.copy(this.focus.position)
      .add(this.scratch.upOffset);
    const compact = !portrait && this.width < 1100;
    const anchorX = this.mobileStage ? 0.5 : portrait ? 0.5 : compact ? 0.27 : 550 / 1920;
    const anchorY = this.mobileStage ? 0.5 : portrait ? 0.27 + 34 / this.height : compact ? 0.49 : 560 / 1080;
    detailAim.addScaledVector(
      right,
      ((0.5 - anchorX) * this.width) / (this.height / span),
    );
    detailAim.addScaledVector(up, (anchorY - 0.5) * span);
    target.lerp(detailAim, this.mobileStage ? 1 : d);
    if (!this.mobileStage && portrait && d < 0.99) target.addScaledVector(up, -1.2 * (1 - d));
    const eye = this.scratch.eye.copy(target).addScaledVector(direction, distance);
    const blend = this.reduced || !this.cameraAim ? 1 : 1 - Math.exp(-seconds * 5);
    this.cameraAim ??= target.clone();
    this.cameraAim.lerp(target, blend);
    this.camera.position.lerp(eye, blend);
    this.camera.lookAt(this.cameraAim);
    this.camera.aspect = aspect;
    this.camera.fov = THREE.MathUtils.lerp(this.camera.fov,
      THREE.MathUtils.radToDeg(2 * Math.atan(span / (2 * distance))), blend);
    this.camera.updateProjectionMatrix();
    this.camera.projectionMatrix.elements[8] += 0.16 * (this.mobileStage ? 0 : 1 - smooth((d - 0.25) / 0.55));
    this.camera.projectionMatrix.elements[9] += 0.2 * (this.mobileStage ? 0 : 1 - smooth((d - 0.25) / 0.55));
    this.camera.projectionMatrixInverse
      .copy(this.camera.projectionMatrix)
      .invert();
    this.scene.fog.near = distance + (this.dark ? 1 : 5);
    this.scene.fog.far =
      distance + THREE.MathUtils.lerp(this.dark ? 24 : 25, 12, d);
    this.warm.position
      .copy(this.focus.position)
      .add(this.scratch.up.set(
        THREE.MathUtils.lerp(-1.5, -3.2, desktopDetail),
        THREE.MathUtils.lerp(3.5, 2.7, desktopDetail),
        THREE.MathUtils.lerp(1.7, 2.1, desktopDetail),
      ));
    if (this.spot) {
      this.spot.position.copy(this.focus.position).add(this.spotOffset.set(-5, 9, 6));
      this.spot.target.position.copy(this.focus.position).add(this.spotTargetOffset.set(0, 1.6, 0));
      this.spot.target.updateMatrixWorld();
    }
    this.camera.updateMatrixWorld();
    this.updateInstances(now / 1000);
    if (this.postEnabled && this.postTarget) {
      this.postMaterial.uniforms.uFocus.value =
        this.camera.position.distanceTo(this.focus.position);
      this.postMaterial.uniforms.uFocusRange.value = this.dark ? 4.2 : 6;
      this.postMaterial.uniforms.uMaxBlur.value = 4.5;
      this.postMaterial.uniforms.uDof.value = this.profile.dof ? 1 : 0;
      this.postMaterial.uniforms.uBloom.value = this.dark ? 0.19 : 0;
      this.postMaterial.uniforms.uVignette.value = this.dark ? 0.2 : 0.1;
      this.renderPost();
    } else {
      this.renderer.render(this.scene, this.camera);
    }
    this.renderCount = (this.renderCount || 0) + 1;
    if (
      now < this.until ||
      Math.abs(this.detail - this.detailTarget) > 0.0001 ||
      this.clarity.moving(this.detailTarget) ||
      (!this.detailTarget && Math.hypot(this.turn.x, this.turn.y) > 0.001) ||
      this.railSpring.moving(-this.selectionStep * 0.62) ||
      this.liftSpring.moving(this.detailTarget ? 4.05 : 0.4) ||
      this.returning.length
    )
      this.invalidate(0);
    else if (this.profile.idle && !this.reduced && !this.mobileStage && !this.detailTarget && this.count)
      this.idleTimer = setTimeout(() => this.invalidate(0), 50);
  }
  events() {
    addEventListener("resize", () => this.resize());
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) this.invalidate();
    });
    const el = this.renderer.domElement;
    el.style.touchAction = "none";
    let down = null;
    let moved = false;
    el.addEventListener("pointerdown", (e) => {
      this.dragging = true;
      down = { x: e.clientX, y: e.clientY, rx: this.turn.x, ry: this.turn.y };
      moved = false;
      if (this.detailTarget) el.setPointerCapture(e.pointerId);
    });
    el.addEventListener("pointermove", (e) => {
      if (!down) return;
      const dx = e.clientX - down.x,
        dy = e.clientY - down.y;
      if (Math.hypot(dx, dy) > 5) moved = true;
      if (this.detailTarget) {
        this.turn = { x: down.rx + dy * 0.009, y: down.ry + dx * 0.009 };
        this.invalidate();
      }
    });
    el.addEventListener("pointerup", (e) => {
      this.dragging = false;
      if (this.detailTarget && moved) {
        this.turn.x = Math.atan2(Math.sin(this.turn.x), Math.cos(this.turn.x));
        this.turn.y = Math.atan2(Math.sin(this.turn.y), Math.cos(this.turn.y));
      }
      this.invalidate(2000);
      if (
        down &&
        moved &&
        !this.detailTarget &&
        Math.abs(e.clientY - down.y) > 40
      ) {
        this.onSelect(this.selected + (e.clientY < down.y ? 1 : -1));
        down = null;
        return;
      }
      if (down && !moved && !this.detailTarget) {
        const pos = new THREE.Vector2(
          ((e.clientX - el.getBoundingClientRect().left) / this.width) * 2 - 1,
          1 - ((e.clientY - el.getBoundingClientRect().top) / this.height) * 2,
        );
        const ray = new THREE.Raycaster();
        ray.setFromCamera(pos, this.camera);
        const hits = ray.intersectObjects([
          ...this.arrays,
          ...this.focus.children,
        ]);
        if (hits.length) {
          const hit = hits[0];
          if (hit.instanceId !== undefined) {
            const cell = hit.object.userData.visibleCells?.[hit.instanceId] || this.cells[hit.instanceId];
            this.onSelect(
              this.selected +
                cell.row +
                Math.round(-this.railSpring.value / 0.62) -
                this.selectionStep,
            );
          } else this.onDetail();
        }
      }
      down = null;
    });
    el.addEventListener("pointercancel", () => { down = null; this.dragging = false; this.invalidate(2000); });
    el.addEventListener(
      "wheel",
      (e) => {
        if (this.detailTarget || !this.count) return;
        e.preventDefault();
        const now = performance.now();
        if (now - (this.lastWheel || 0) < 150) return;
        this.lastWheel = now;
        this.onSelect(this.selected + (e.deltaY > 0 ? 1 : -1));
      },
      { passive: false },
    );
  }
}
